import React, { useState, useCallback, useRef, useEffect } from 'react'
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Panel,
  BackgroundVariant
} from 'reactflow'
import 'reactflow/dist/style.css'
import { TriggerNode, ActionNode, ConditionNode, DelayNode, DataNode, EndNode } from './workflow-builder/CustomNodes'
import NodeConfigModal from './workflow-builder/NodeConfigModal'
import toast from 'react-hot-toast'
import workflowEngine from '../services/WorkflowExecutionEngine'
import eventBus, { EventTypes } from '../services/EventBus'
import leadToContactSync from '../services/LeadToContactSync'
import workflowService from '../services/workflowService'
import { useTenant } from '../contexts/TenantContext'
import { useAuth } from '../contexts/AuthContext'
import AIService from '../services/aiService'

// Register custom node types
const nodeTypes = {
  triggerNode: TriggerNode,
  actionNode: ActionNode,
  conditionNode: ConditionNode,
  delayNode: DelayNode,
  dataNode: DataNode,
  endNode: EndNode,
}

// Node templates for the sidebar
const nodeTemplates = {
  triggers: [
    { type: 'triggerNode', icon: '🎯', label: 'New Lead Added', triggerType: 'lead_added' },
    { type: 'triggerNode', icon: '📧', label: 'Email Opened', triggerType: 'email_opened' },
    { type: 'triggerNode', icon: '🔗', label: 'Link Clicked', triggerType: 'email_clicked' },
    { type: 'triggerNode', icon: '💬', label: 'Email Replied', triggerType: 'email_replied' },
    { type: 'triggerNode', icon: '📝', label: 'Form Submitted', triggerType: 'form_submitted' },
    { type: 'triggerNode', icon: '⏰', label: 'Time Schedule', triggerType: 'schedule' },
    { type: 'triggerNode', icon: '🏷️', label: 'Tag Added', triggerType: 'tag_added' },
    { type: 'triggerNode', icon: '📊', label: 'Deal Stage Changed', triggerType: 'deal_stage_changed' },
    { type: 'triggerNode', icon: '🎉', label: 'Deal Won', triggerType: 'deal_won' },
    { type: 'triggerNode', icon: '❌', label: 'Deal Lost', triggerType: 'deal_lost' },
    { type: 'triggerNode', icon: '⏳', label: 'Deal Stuck', triggerType: 'deal_stuck' },
  ],
  actions: [
    { type: 'actionNode', icon: '✉️', label: 'Send Email', actionType: 'send_email' },
    { type: 'actionNode', icon: '📞', label: 'Send SMS', actionType: 'send_sms' },
    { type: 'actionNode', icon: '➕', label: 'Add to List', actionType: 'add_to_list' },
    { type: 'actionNode', icon: '✅', label: 'Validate Email', actionType: 'validate_email' },
    { type: 'actionNode', icon: '🏢', label: 'Create Deal', actionType: 'create_deal' },
    { type: 'actionNode', icon: '📅', label: 'Book Appointment', actionType: 'book_appointment' },
    { type: 'actionNode', icon: '🔔', label: 'Send Notification', actionType: 'send_notification' },
    { type: 'actionNode', icon: '🔗', label: 'Webhook', actionType: 'webhook' },
  ],
  logic: [
    { type: 'conditionNode', icon: '🔀', label: 'If/Else Condition', description: 'Branch based on condition' },
    { type: 'delayNode', icon: '⏸️', label: 'Wait/Delay', description: 'Pause before next step' },
    { type: 'endNode', icon: '🛑', label: 'Stop Workflow', description: 'End execution' },
  ],
  data: [
    { type: 'dataNode', icon: '🏷️', label: 'Add Tag', dataOperation: 'add_tag' },
    { type: 'dataNode', icon: '📊', label: 'Update Field', dataOperation: 'update_field' },
    { type: 'dataNode', icon: '📈', label: 'Update Score', dataOperation: 'update_score' },
    { type: 'dataNode', icon: '💾', label: 'Save to Database', dataOperation: 'save_to_db' },
    { type: 'dataNode', icon: '🗑️', label: 'Remove Tag', dataOperation: 'remove_tag' },
  ],
}

const VisualWorkflowBuilder = () => {
  const { tenant } = useTenant()
  const { user } = useAuth()
  const reactFlowWrapper = useRef(null)
  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])
  const [reactFlowInstance, setReactFlowInstance] = useState(null)
  const [selectedNode, setSelectedNode] = useState(null)
  const [showConfigModal, setShowConfigModal] = useState(false)
  const [workflowName, setWorkflowName] = useState('Untitled Workflow')
  const [workflowList, setWorkflowList] = useState([])
  const [currentWorkflowId, setCurrentWorkflowId] = useState(null)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showAIModal, setShowAIModal] = useState(false)
  const [aiPrompt, setAIPrompt] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)

  // Load saved workflows from Firebase on mount
  useEffect(() => {
    const loadWorkflows = async () => {
      if (!tenant?.id) return
      
      setIsLoading(true)
      const result = await workflowService.getWorkflows(tenant.id)
      
      if (result.success) {
        setWorkflowList(result.data)
        console.log(`✅ Loaded ${result.data.length} workflows from Firebase`)
      } else {
        console.error('❌ Failed to load workflows:', result.error)
        toast.error('Failed to load workflows')
      }
      setIsLoading(false)
    }

    loadWorkflows()

    // Initialize workflow execution engine with tenant ID for Firebase sync
    if (tenant?.id) {
      workflowEngine.initialize(tenant.id)
    }
    
    // Initialize lead-to-contact auto-sync
    leadToContactSync.initialize()

    console.log('✅ Workflow Builder initialized with execution engine')
  }, [tenant?.id])

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({ ...params, animated: true, style: { stroke: '#6366f1', strokeWidth: 2 } }, eds)),
    [setEdges]
  )

  const onDragOver = useCallback((event) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }, [])

  const onDrop = useCallback(
    (event) => {
      event.preventDefault()

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect()
      const nodeData = JSON.parse(event.dataTransfer.getData('application/reactflow'))

      if (!nodeData) return

      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      })

      const newNode = {
        id: `${nodeData.type}-${Date.now()}`,
        type: nodeData.type,
        position,
        data: { ...nodeData, config: {} },
      }

      setNodes((nds) => nds.concat(newNode))
      toast.success('Node added to canvas')
    },
    [reactFlowInstance, setNodes]
  )

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node)
    setShowConfigModal(true)
  }, [])

  const onNodeConfigSave = useCallback((nodeId, config) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              config,
              // Update label with config details
              description: config.delayAmount ? `${config.delayAmount} ${config.delayUnit}` : node.data.description,
            },
          }
        }
        return node
      })
    )
  }, [setNodes])

  const saveWorkflow = useCallback(async () => {
    if (!tenant?.id) {
      toast.error('Please sign in to save workflows')
      return
    }

    if (!workflowName.trim()) {
      toast.error('Please enter a workflow name')
      return
    }

    setIsLoading(true)

    const workflow = {
      id: currentWorkflowId,
      name: workflowName,
      nodes,
      edges,
      status: 'active' // Ensure new workflows are active by default
    }

    // Save to Firebase (master copy)
    const result = await workflowService.saveWorkflow(tenant.id, workflow)

    if (result.success) {
      toast.success(result.isUpdate ? 'Workflow updated!' : 'Workflow saved!')
      setCurrentWorkflowId(result.data.id)
      
      // ALSO save to localStorage for immediate execution engine availability
      const existingWorkflows = JSON.parse(localStorage.getItem('visual_workflows') || '[]')
      const updatedWorkflow = {
        ...result.data,
        id: result.data.id
      }
      
      const index = existingWorkflows.findIndex(w => w.id === updatedWorkflow.id)
      if (index >= 0) {
        existingWorkflows[index] = updatedWorkflow
      } else {
        existingWorkflows.push(updatedWorkflow)
      }
      
      localStorage.setItem('visual_workflows', JSON.stringify(existingWorkflows))
      console.log('✅ Workflow saved to Firebase AND localStorage')
      
      // Reload workflow list
      const listResult = await workflowService.getWorkflows(tenant.id)
      if (listResult.success) {
        setWorkflowList(listResult.data)
      }

      // Reload workflows in execution engine (will now pick up the change)
      workflowEngine.initialize(tenant.id) // Re-initialize with tenantId
    } else {
      toast.error(`Failed to save: ${result.error}`)
    }

    setIsLoading(false)
  }, [tenant?.id, workflowName, nodes, edges, currentWorkflowId])

  const loadWorkflow = useCallback(async (workflowId) => {
    if (!tenant?.id) return

    setIsLoading(true)
    const result = await workflowService.getWorkflow(tenant.id, workflowId)

    if (result.success) {
      const workflow = result.data
      setNodes(workflow.nodes || [])
      setEdges(workflow.edges || [])
      setWorkflowName(workflow.name)
      setCurrentWorkflowId(workflow.id)
      toast.success(`Loaded: ${workflow.name}`)
    } else {
      toast.error(`Failed to load: ${result.error}`)
    }

    setIsLoading(false)
  }, [tenant?.id, setNodes, setEdges])

  const newWorkflow = useCallback(() => {
    setNodes([])
    setEdges([])
    setWorkflowName('Untitled Workflow')
    setCurrentWorkflowId(null)
    toast.success('New workflow created')
  }, [setNodes, setEdges])

  const deleteWorkflow = useCallback(async (workflowId) => {
    if (!tenant?.id) return

    setIsLoading(true)
    const result = await workflowService.deleteWorkflow(tenant.id, workflowId)

    if (result.success) {
      toast.success('Workflow deleted')
      
      // Reload workflow list
      const listResult = await workflowService.getWorkflows(tenant.id)
      if (listResult.success) {
        setWorkflowList(listResult.data)
      }
      
      if (currentWorkflowId === workflowId) {
        newWorkflow()
      }
    } else {
      toast.error(`Failed to delete: ${result.error}`)
    }

    setIsLoading(false)
  }, [tenant?.id, currentWorkflowId, newWorkflow])

  const onDragStart = (event, nodeData) => {
    event.dataTransfer.setData('application/reactflow', JSON.stringify(nodeData))
    event.dataTransfer.effectAllowed = 'move'
  }

  const testWorkflow = useCallback(() => {
    if (nodes.length === 0) {
      toast.error('Please add nodes to the canvas first')
      return
    }

    // Simulate a lead_added event
    const testData = {
      id: `test_lead_${Date.now()}`,
      firstName: 'John',
      lastName: 'Doe',
      email: 'john.doe@example.com',
      companyName: 'Acme Corp',
      industry: 'Technology',
      leadScore: 50,
      source: 'test'
    }

    toast.success('Triggering test event: New Lead Added')
    eventBus.emit(EventTypes.LEAD_ADDED, testData)
  }, [nodes])

  const generateWorkflowWithAI = useCallback(async () => {
    if (!aiPrompt.trim()) {
      toast.error('Please describe the workflow you want to build')
      return
    }

    if (!user?.uid) {
      toast.error('Please sign in to use AI generation')
      return
    }

    setIsGenerating(true)

    try {
      const result = await AIService.generateWorkflowFromPrompt(user.uid, aiPrompt, tenant?.id)

      if (result.success) {
        const { workflow } = result

        // Convert AI workflow to React Flow format
        const generatedNodes = workflow.nodes.map((node, index) => ({
          id: node.id,
          type: node.type,
          position: { x: 100 + (index * 250), y: 100 + (Math.floor(index / 4) * 150) },
          data: {
            label: node.label,
            icon: getIconForNodeType(node),
            config: node.config || {},
            ...node
          }
        }))

        const generatedEdges = workflow.connections.map((conn, index) => ({
          id: `edge-${index}`,
          source: conn.from,
          target: conn.to,
          animated: true,
          style: { stroke: '#6366f1', strokeWidth: 2 }
        }))

        setNodes(generatedNodes)
        setEdges(generatedEdges)
        setWorkflowName(workflow.workflowName || 'AI Generated Workflow')
        setShowAIModal(false)
        setAIPrompt('')

        toast.success(`✨ ${workflow.workflowName} generated! Review and customize as needed.`, { duration: 5000 })
      } else {
        toast.error(`Failed to generate: ${result.error}`)
      }
    } catch (error) {
      console.error('AI Generation Error:', error)
      toast.error(error.message || 'Failed to generate workflow')
    } finally {
      setIsGenerating(false)
    }
  }, [aiPrompt, user, tenant, setNodes, setEdges])

  // Helper function to get icon for node type
  const getIconForNodeType = (node) => {
    if (node.type === 'triggerNode') {
      const triggers = {
        lead_added: '🎯',
        email_opened: '📧',
        email_clicked: '🔗',
        email_replied: '💬',
        form_submitted: '📝',
        schedule: '⏰',
        tag_added: '🏷️',
        deal_stage_changed: '📊',
        deal_won: '🎉',
        deal_lost: '❌',
        deal_stuck: '⏳'
      }
      return triggers[node.config?.triggerType] || '🎯'
    } else if (node.type === 'actionNode') {
      const actions = {
        send_email: '✉️',
        send_sms: '📞',
        add_to_list: '➕',
        validate_email: '✅',
        create_deal: '🏢',
        book_appointment: '📅',
        send_notification: '🔔',
        webhook: '🔗'
      }
      return actions[node.config?.actionType] || '✉️'
    } else if (node.type === 'dataNode') {
      const dataOps = {
        add_tag: '🏷️',
        update_field: '📊',
        update_score: '📈',
        save_to_db: '💾',
        remove_tag: '🗑️'
      }
      return dataOps[node.config?.dataOperation] || '🏷️'
    } else if (node.type === 'conditionNode') {
      return '🔀'
    } else if (node.type === 'delayNode') {
      return '⏸️'
    } else if (node.type === 'endNode') {
      return '🛑'
    }
    return '⚡'
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Left Sidebar - Node Library */}
      <div className={`${sidebarCollapsed ? 'w-16' : 'w-72'} bg-white border-r border-gray-200 flex flex-col transition-all duration-300 overflow-hidden`}>
        <div className="p-3 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
          {!sidebarCollapsed && (
            <>
              <h2 className="text-lg font-semibold">Workflow Builder</h2>
              <p className="text-xs opacity-90">Drag nodes to canvas</p>
            </>
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="absolute top-4 right-4 text-white hover:bg-white hover:bg-opacity-20 rounded p-1"
          >
            {sidebarCollapsed ? '→' : '←'}
          </button>
        </div>

        {!sidebarCollapsed && (
          <div className="flex-1 overflow-y-auto p-3 space-y-5">
            {/* Trigger Nodes */}
            <div>
              <h3 className="text-xs font-bold text-purple-700 uppercase mb-2 flex items-center gap-2">
                <span className="w-3 h-3 bg-purple-400 rounded-full"></span>
                Triggers
              </h3>
              <div className="space-y-2">
                {nodeTemplates.triggers.map((node, idx) => (
                  <div
                    key={idx}
                    draggable
                    onDragStart={(e) => onDragStart(e, node)}
                    className="flex items-center gap-2 p-2.5 bg-purple-50 border border-purple-200 rounded-lg cursor-move hover:bg-purple-100 hover:border-purple-300 transition"
                  >
                    <span className="text-lg">{node.icon}</span>
                    <span className="text-sm font-medium text-gray-800">{node.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Nodes */}
            <div>
              <h3 className="text-xs font-bold text-blue-700 uppercase mb-2 flex items-center gap-2">
                <span className="w-3 h-3 bg-blue-400 rounded-full"></span>
                Actions
              </h3>
              <div className="space-y-2">
                {nodeTemplates.actions.map((node, idx) => (
                  <div
                    key={idx}
                    draggable
                    onDragStart={(e) => onDragStart(e, node)}
                    className="flex items-center gap-2 p-2.5 bg-blue-50 border border-blue-200 rounded-lg cursor-move hover:bg-blue-100 hover:border-blue-300 transition"
                  >
                    <span className="text-lg">{node.icon}</span>
                    <span className="text-sm font-medium text-gray-800">{node.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Logic Nodes */}
            <div>
              <h3 className="text-xs font-bold text-yellow-700 uppercase mb-2 flex items-center gap-2">
                <span className="w-3 h-3 bg-yellow-400 rounded-full"></span>
                Logic
              </h3>
              <div className="space-y-2">
                {nodeTemplates.logic.map((node, idx) => (
                  <div
                    key={idx}
                    draggable
                    onDragStart={(e) => onDragStart(e, node)}
                    className="flex items-center gap-2 p-2.5 bg-yellow-50 border border-yellow-200 rounded-lg cursor-move hover:bg-yellow-100 hover:border-yellow-300 transition"
                  >
                    <span className="text-lg">{node.icon}</span>
                    <span className="text-sm font-medium text-gray-800">{node.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Data Nodes */}
            <div>
              <h3 className="text-xs font-bold text-green-700 uppercase mb-2 flex items-center gap-2">
                <span className="w-3 h-3 bg-green-400 rounded-full"></span>
                Data Operations
              </h3>
              <div className="space-y-2">
                {nodeTemplates.data.map((node, idx) => (
                  <div
                    key={idx}
                    draggable
                    onDragStart={(e) => onDragStart(e, node)}
                    className="flex items-center gap-2 p-2.5 bg-green-50 border border-green-200 rounded-lg cursor-move hover:bg-green-100 hover:border-green-300 transition"
                  >
                    <span className="text-lg">{node.icon}</span>
                    <span className="text-sm font-medium text-gray-800">{node.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Saved Workflows */}
            <div>
              <h3 className="text-xs font-bold text-gray-700 uppercase mb-2">Saved Workflows</h3>
              {isLoading ? (
                <p className="text-xs text-gray-500 italic">⏳ Loading workflows...</p>
              ) : workflowList.length === 0 ? (
                <p className="text-xs text-gray-500 italic">No saved workflows yet</p>
              ) : (
                <div className="space-y-2">
                  {workflowList.map((workflow) => (
                    <div
                      key={workflow.id}
                      className="p-2 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition"
                    >
                      <div className="flex items-center justify-between">
                        <button
                          onClick={() => loadWorkflow(workflow.id)}
                          className="text-sm font-medium text-gray-800 hover:text-indigo-600 flex-1 text-left"
                          disabled={isLoading}
                        >
                          {workflow.name}
                        </button>
                        <button
                          onClick={() => deleteWorkflow(workflow.id)}
                          className="text-red-500 hover:text-red-700 text-xs px-2 disabled:opacity-50"
                          title="Delete"
                          disabled={isLoading}
                        >
                          🗑️
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {workflow.updatedAt?.seconds 
                          ? new Date(workflow.updatedAt.seconds * 1000).toLocaleDateString()
                          : workflow.updatedAt 
                          ? new Date(workflow.updatedAt).toLocaleDateString()
                          : 'Unknown date'}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Main Canvas Area */}
      <div className="flex-1 flex flex-col">
        {/* Top Toolbar */}
        <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <input
              type="text"
              value={workflowName}
              onChange={(e) => setWorkflowName(e.target.value)}
              className="text-lg font-semibold bg-transparent border-b-2 border-transparent hover:border-gray-300 focus:border-indigo-500 focus:outline-none px-2 py-1"
              placeholder="Workflow name..."
            />
            {currentWorkflowId && (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Saved</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAIModal(true)}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 font-medium transition shadow-md"
              disabled={isLoading}
            >
              ✨ Generate with AI
            </button>
            <button
              onClick={newWorkflow}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition"
              disabled={isLoading}
            >
              🆕 New
            </button>
            <button
              onClick={saveWorkflow}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? '⏳ Saving...' : '💾 Save Workflow'}
            </button>
            <button
              onClick={testWorkflow}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition"
              disabled={isLoading}
            >
              🧪 Test Workflow
            </button>
            <button
              onClick={() => {
                setNodes([])
                setEdges([])
                toast.success('Canvas cleared')
              }}
              className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-medium transition"
            >
              🗑️ Clear
            </button>
          </div>
        </div>

        {/* React Flow Canvas */}
        <div ref={reactFlowWrapper} className="flex-1">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={setReactFlowInstance}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            fitView
            className="bg-gray-50"
          >
            <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#e5e7eb" />
            <Controls />
            <MiniMap
              nodeColor={(node) => {
                switch (node.type) {
                  case 'triggerNode': return '#c084fc'
                  case 'actionNode': return '#60a5fa'
                  case 'conditionNode': return '#fbbf24'
                  case 'delayNode': return '#fb923c'
                  case 'dataNode': return '#4ade80'
                  default: return '#9ca3af'
                }
              }}
              className="bg-white border border-gray-300 rounded-lg"
            />
            <Panel position="top-right" className="bg-white rounded-lg shadow-lg p-4 m-4 max-w-xs">
              <h4 className="font-bold text-sm mb-2 text-gray-800">💡 Quick Guide</h4>
              <ul className="text-xs text-gray-600 space-y-1">
                <li>• Drag nodes from sidebar onto canvas</li>
                <li>• Click & drag between node ports to connect</li>
                <li>• Click on any node to configure settings</li>
                <li>• Save your workflow when done</li>
              </ul>
            </Panel>
          </ReactFlow>
        </div>
      </div>

      {/* AI Workflow Generator Modal */}
      {showAIModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <span className="text-3xl">✨</span>
                  AI Workflow Generator
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Describe your workflow in plain English, and AI will build it for you!
                </p>
              </div>
              <button
                onClick={() => {
                  setShowAIModal(false)
                  setAIPrompt('')
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                disabled={isGenerating}
              >
                ×
              </button>
            </div>

            {/* Example Prompts */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-sm text-purple-900 mb-2">💡 Example Prompts:</h3>
              <div className="space-y-2 text-sm text-gray-700">
                <button
                  onClick={() => setAIPrompt("When someone submits a contact form, send them a welcome email immediately, wait 2 days, then send a follow-up email with resources")}
                  className="block w-full text-left p-2 bg-white rounded border border-purple-200 hover:bg-purple-50 transition"
                >
                  "When someone submits a contact form, send them a welcome email immediately, wait 2 days, then send a follow-up email with resources"
                </button>
                <button
                  onClick={() => setAIPrompt("Create an abandoned cart workflow: when a tag 'Cart Abandoned' is added, wait 1 hour, send reminder email, wait 1 day, check if they purchased, if not send discount offer")}
                  className="block w-full text-left p-2 bg-white rounded border border-purple-200 hover:bg-purple-50 transition"
                >
                  "Create an abandoned cart workflow: when a tag 'Cart Abandoned' is added, wait 1 hour, send reminder email, wait 1 day, check if they purchased, if not send discount offer"
                </button>
                <button
                  onClick={() => setAIPrompt("When a new lead is added, add them to the welcome list, send a welcome email, wait 3 days, add a 'Nurture' tag")}
                  className="block w-full text-left p-2 bg-white rounded border border-purple-200 hover:bg-purple-50 transition"
                >
                  "When a new lead is added, add them to the welcome list, send a welcome email, wait 3 days, add a 'Nurture' tag"
                </button>
              </div>
            </div>

            {/* Prompt Input */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Describe Your Workflow:
              </label>
              <textarea
                value={aiPrompt}
                onChange={(e) => setAIPrompt(e.target.value)}
                placeholder="Example: When someone opens my email, wait 2 days, then send a follow-up if they didn't click any links..."
                className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                disabled={isGenerating}
              />
              <p className="text-xs text-gray-500 mt-1">
                Be as detailed as you like! Include triggers, actions, delays, and conditions.
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={generateWorkflowWithAI}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                disabled={isGenerating || !aiPrompt.trim()}
              >
                {isGenerating ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </span>
                ) : (
                  '✨ Generate Workflow'
                )}
              </button>
              <button
                onClick={() => {
                  setShowAIModal(false)
                  setAIPrompt('')
                }}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-semibold transition"
                disabled={isGenerating}
              >
                Cancel
              </button>
            </div>

            {/* Info Box */}
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800">
                <strong>ℹ️ Note:</strong> The AI will create the basic workflow structure. You can then click on each node to customize details like email content, subject lines, exact timing, etc.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Node Configuration Modal */}
      <NodeConfigModal
        node={selectedNode}
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        onSave={onNodeConfigSave}
      />
    </div>
  )
}

export default VisualWorkflowBuilder
